<template>
  <main>
    <LoaderOverlay :active="isLoading" />

    <div v-if="!isLoading && loadError" class="text-center py-[6.4rem]">
      {{ $t("Content temporarily unavailable") }}
    </div>

    <div v-else-if="!isLoading && doctor">
      <LayoutSection
        :maxWidthClass="'max-w-[1100px] mx-auto'"
        paddingClass="pt-0 pb-10 sm:pb-12 md:pb-14 lg:pb-16"
        sectionClass="mt-4"
      >
        <router-link to="/search/" class="profile-back mb-4">
          <svg
            class="profile-back__icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ $t("Back to search") }}
        </router-link>

        <div class="profile-tabs" role="tablist">
          <button
            v-for="tab in profileTabs"
            :key="tab.id"
            type="button"
            role="tab"
            :aria-selected="activeProfileTab === tab.id"
            class="profile-tabs__tab"
            :class="{ 'profile-tabs__tab--active': activeProfileTab === tab.id }"
            @click="activeProfileTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="mt-6">
          <!-- Booking tab: doctor card, map and schedule. Kept mounted (v-show)
               so switching tabs does not reload the calendar/map. -->
          <div v-show="activeProfileTab === 'reserva'">
            <MainContent class="space-y-6">
              <div
                class="grid grid-cols-1 gap-4"
                :class="{ 'lg:grid-cols-2': hasMap }"
              >
                <div
                  class="order-1 sm:mb-4 lg:mb-0"
                  :class="{ 'lg:col-span-2': !hasMap }"
                >
                  <SubContent paddingClass="p-[1.6rem] h-full">
                    <DoctorComponent
                      :doctor="doctor.user"
                      :show-about-me="false"
                      :show-doctor-reviews="false"
                      :active-booking-key="selectedBookingKey"
                      @select-booking-option="handleBookingOptionSelection"
                    />
                  </SubContent>
                </div>
                <div
                  v-if="hasMap"
                  class="order-3 lg:order-2 sm:mb-4 lg:mb-0"
                >
                  <div
                    class="bg-white backdrop-blur-[2px] h-full relative p-[1.2rem] sm:p-[1.2rem] rounded-[2.4rem] border border-white w-full min-h-[28rem] sm:min-h-[32rem] md:min-h-[29rem] mt-3 lg:mt-0"
                  >
                    <GoogleMaps
                      :doctors="mapDoctors.length ? mapDoctors : [doctor.user]"
                      class="w-full h-[28rem] sm:h-[32rem] rounded-[1.6rem] overflow-hidden"
                      ref="mapRef"
                    />
                    <MapExpandButton
                      class="absolute left-1/2 top-12 -translate-x-1/2"
                      @click="viewMapModal"
                    />
                  </div>
                </div>

                <div
                  v-if="hasDoctorSchedule"
                  class="order-2 lg:order-3"
                  :class="{ 'lg:col-span-2': hasMap }"
                >
                  <SubContent>
                    <DoctorSchedule
                      :doctor="doctorSchedule"
                      :selected-booking-key="selectedBookingKey || undefined"
                      :base-date="doctorSchedule?.baseDateIso || undefined"
                      :schedule-version="doctorSchedule?.scheduleVersion || 0"
                      @booking-option-change="onScheduleBookingOptionChange"
                      @request-more-dates="handleScheduleMoreDates"
                      @request-calendar="handleScheduleCalendar"
                    />
                  </SubContent>
                </div>
              </div>
            </MainContent>
          </div>

          <MainContent v-if="activeProfileTab === 'experience'">
            <SubContent>
              <WorkExperience
                :aboutMe="aboutMeList"
                :education="educationList"
                :show-about-section="showAboutSection"
                :show-title="false"
              />
            </SubContent>
          </MainContent>

          <MainContent v-if="activeProfileTab === 'services'">
            <SubContent>
              <Services :services="servicesList" :show-title="false" />
            </SubContent>
          </MainContent>

          <MainContent v-if="activeProfileTab === 'sessions'">
            <SubContent>
              <ClassCardsList
                :items="classes"
                :title="''"
                :show-collapse="false"
              >
                <template #cta="{ classData, navigate }">
                  <div class="flex flex-col gap-[0.8rem]">
                    <ClassJoinButton
                      :class-data="classData"
                      :label="$t('Book now')"
                      :block="true"
                    />
                    <AppButton
                      :label="$t('More information')"
                      variant="secondary"
                      class="class-card__more-button"
                      :block="true"
                      @click="navigate()"
                    />
                  </div>
                </template>
              </ClassCardsList>
            </SubContent>
          </MainContent>
        </div>
      </LayoutSection>

      <MapModal
        v-if="isMapModalVisible"
        :doctors="mapDoctors"
        :dates-loading="activeScheduleLoading"
        :selected-booking-keys="mapSelectedBookingKeys"
        @close="closeMapModal"
        @select-booking-option="onMapBookingOptionSelection"
        @request-calendar="onMapScheduleCalendar"
        @request-more-dates="onMapScheduleMoreDates"
      />
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { setSeo, jsonLd } from "@/composables/useSeo";

import LoaderOverlay from "@/components/loader/loader.vue";
import LayoutSection from "@/components/design/layoutSection.vue";
import MainContent from "@/components/design/mainContent.vue";
import SubContent from "@/components/design/subContent.vue";
import AppButton from "@/components/buttons/AppButton.vue";
import DoctorComponent from "@/components/doctor/doctorComponent.vue";
import DoctorSchedule from "@/components/search/doctorSchedule.vue";
import GoogleMaps from "@/components/doctor/googleMaps.vue";
import MapExpandButton from "@/components/buttons/MapExpandButton.vue";
import MapModal from "@/components/search/mapModal.vue";
import WorkExperience from "@/components/doctor/workExperience.vue";
import Services from "@/components/doctor/services.vue";
import ClassCardsList from "@/components/classes/ClassCardsList.vue";
import ClassJoinButton from "@/components/classes/ClassJoinButton.vue";
import { useProfile } from "@/composables";
import type { CalendarRequestPayload } from "@/types/domain";

const { t } = useI18n();
const mapRef = ref<typeof GoogleMaps | null>(null);

// Use the refactored composable
const {
  isMapModalVisible,
  mapDoctors,
  doctor,
  doctorSchedule,
  classes,
  isLoading,
  loadError,
  selectedBookingKey,
  hasDoctorSchedule,
  activeScheduleLoading,
  mapSelectedBookingKeys,
  aboutMeList,
  educationList,
  servicesList,
  showAboutSection,
  hasExperienceSection,
  hasServicesSection,
  hasMap,
  handleBookingOptionSelection,
  handleScheduleMoreDates,
  handleScheduleCalendar,
  viewMapModal,
  closeMapModal,
} = useProfile({
  translateFn: t,
});

// Single column with tabs: Booking (default) / Experience / Services / Sessions
const activeProfileTab = ref("reserva");
const profileTabs = computed(() => {
  const list: { id: string; label: string }[] = [
    { id: "reserva", label: t("Booking") },
  ];
  if (hasExperienceSection.value)
    list.push({ id: "experience", label: t("Experience") });
  if (hasServicesSection.value)
    list.push({ id: "services", label: t("services") });
  if (classes.value.length)
    list.push({ id: "sessions", label: t("Upcoming sessions") });
  return list;
});
watch(
  profileTabs,
  (list) => {
    if (!list.some((tab) => tab.id === activeProfileTab.value)) {
      activeProfileTab.value = list.length ? list[0].id : "";
    }
  },
  { immediate: true },
);

// Dynamic SEO — fires when the doctor profile finishes loading
watch(doctor, (d) => {
  if (!d?.user) return;
  const u = d.user;
  const name: string = u.name ?? u.fullName ?? "";
  const specialty: string = u.specialty ?? u.category ?? "";
  const aboutMeText: string = Array.isArray(u.aboutMe)
    ? u.aboutMe.filter(Boolean).join(" ")
    : typeof u.aboutMe === "string"
      ? u.aboutMe
      : "";
  const description: string =
    aboutMeText.trim() ||
    (specialty
      ? `${name}, especialista en ${specialty} en Medkus.`
      : `Perfil de ${name} en Medkus.`);
  setSeo({
    title: specialty ? `${name} · ${specialty}` : name,
    description: description.slice(0, 160),
    image: u.image || u.photo || undefined,
    type: "profile",
    jsonLd: jsonLd.medicalBusiness({
      name,
      description,
      image: u.image || u.photo || undefined,
      specialty,
      url: window.location.href,
    }),
  });
});

// Event handlers
function onScheduleBookingOptionChange(bookingKey: string) {
  handleBookingOptionSelection(bookingKey, { source: "schedule" });
}

function onMapBookingOptionSelection(doctor: any, bookingKey: string) {
  handleBookingOptionSelection(bookingKey, { source: "map" });
}

function onMapScheduleCalendar(doctor: any, payload: CalendarRequestPayload) {
  handleScheduleCalendar(payload);
}

function onMapScheduleMoreDates(doctor: any, payload: CalendarRequestPayload) {
  handleScheduleMoreDates(payload);
}
</script>

<style scoped>
/* Quiet back link: orientation without competing with the segmented tabs */
.profile-back {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.18s ease, gap 0.18s ease;
}

.profile-back:hover {
  color: #3346d3;
  gap: 0.6rem;
}

.profile-back__icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
}

/* Segmented control: recessed track with a brand-filled pill on the active tab */
.profile-tabs {
  display: flex;
  gap: 0.4rem;
  padding: 0.5rem;
  background: #eceef4;
  border-radius: 1.4rem;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.06);
  overflow-x: auto;
  scrollbar-width: none;
}

.profile-tabs::-webkit-scrollbar {
  display: none;
}

.profile-tabs__tab {
  flex: 1 1 auto;
  min-width: max-content;
  padding: 0.9rem 1.6rem;
  border-radius: 1rem;
  font-size: 1.35rem;
  font-weight: 600;
  color: #4b5563;
  background: transparent;
  border: 0;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, box-shadow 0.25s ease,
    transform 0.2s ease;
}

.profile-tabs__tab:not(.profile-tabs__tab--active):hover {
  color: #3346d3;
  background: rgba(255, 255, 255, 0.6);
}

.profile-tabs__tab--active {
  color: #ffffff;
  background: #5b6fee;
  box-shadow: 0 6px 16px -6px rgba(91, 111, 238, 0.6);
}
</style>
